interface EOLBadgeProps {
  eolDate: string | null;
  supportDate: string | null;
  isActive: boolean;
  isLTS?: boolean;
  extendedSupport?: boolean;
  className?: string;
}

interface EOLStatus {
  label: string;
  className: string;
  timeRemaining: string;
}

function calculateEOLStatus(
  eolDate: string | null,
  _supportDate: string | null,
  isActive: boolean
): EOLStatus {
  const now = new Date();

  // If explicitly marked as active or no EOL date, it's supported
  if (isActive && !eolDate) {
    return {
      label: 'Supported',
      className: 'bg-green-500/20 text-green-400 border border-green-500/30',
      timeRemaining: 'Active support',
    };
  }

  if (!eolDate) {
    return {
      label: 'Supported',
      className: 'bg-green-500/20 text-green-400 border border-green-500/30',
      timeRemaining: 'No EOL date set',
    };
  }

  const eolDateTime = new Date(eolDate);
  const daysUntilEOL = Math.floor(
    (eolDateTime.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Already EOL
  if (daysUntilEOL < 0) {
    const daysAgo = Math.abs(daysUntilEOL);
    const yearsAgo = Math.floor(daysAgo / 365);
    const monthsAgo = Math.floor((daysAgo % 365) / 30);

    let timeStr = '';
    if (yearsAgo > 0) {
      timeStr = `${yearsAgo} year${yearsAgo > 1 ? 's' : ''} ago`;
    } else if (monthsAgo > 0) {
      timeStr = `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
    } else {
      timeStr = `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
    }

    return {
      label: 'End of Life',
      className: 'bg-red-500/20 text-red-400 border border-red-500/30',
      timeRemaining: `EOL ${timeStr}`,
    };
  }

  // Calculate time remaining
  const years = Math.floor(daysUntilEOL / 365);
  const months = Math.floor((daysUntilEOL % 365) / 30);
  const days = daysUntilEOL % 30;

  let timeStr = '';
  if (years > 0) {
    timeStr = `${years} year${years > 1 ? 's' : ''}`;
    if (months > 0) {
      timeStr += `, ${months} month${months > 1 ? 's' : ''}`;
    }
  } else if (months > 0) {
    timeStr = `${months} month${months > 1 ? 's' : ''}`;
    if (days > 0) {
      timeStr += `, ${days} day${days > 1 ? 's' : ''}`;
    }
  } else {
    timeStr = `${daysUntilEOL} day${daysUntilEOL > 1 ? 's' : ''}`;
  }

  // Risk level
  if (daysUntilEOL <= 30) {
    return {
      label: 'Critical',
      className: 'bg-red-500/20 text-red-400 border border-red-500/30',
      timeRemaining: `${timeStr} remaining`,
    };
  }

  if (daysUntilEOL <= 90) {
    return {
      label: 'Warning',
      className: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      timeRemaining: `${timeStr} remaining`,
    };
  }

  return {
    label: 'Supported',
    className: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    timeRemaining: `${timeStr} remaining`,
  };
}

export function EOLBadge({
  eolDate,
  supportDate,
  isActive,
  isLTS,
  extendedSupport,
  className = '',
}: EOLBadgeProps) {
  const status = calculateEOLStatus(eolDate, supportDate, isActive);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.className}`}>
        {status.label}
        {Boolean(isLTS) && ' (LTS)'}
        {Boolean(extendedSupport) && ' (Extended)'}
      </span>
      <span className="text-sm text-gray-300">{status.timeRemaining}</span>
    </div>
  );
}

export default EOLBadge;
